import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { Avatar } from '@/features/auth/Avatar'
import { fetchProfile, fetchUser, removeAvatar, uploadAvatar } from '@/features/auth/api'
import type { Profile, Rival } from '@/features/auth/types'
import { meKey } from '@/features/auth/useAuth'
import { Face } from '@/features/game/Dice'
import { PlayersIcon } from '@/features/lobby/icons'
import { SettingsCard } from './SettingsCard'

type Props = {
  /** Чей профиль. null — свой. */
  userId: number | null
  onOpenGame: (gameId: string) => void
}

/**
 * Профиль: сколько сыграно, сколько выиграно и с кем.
 *
 * Считает всё сервер (см. App\Services\Social\PlayerStats): доля побед,
 * посчитанная ещё и здесь, разошлась бы с той на округлении.
 */
export function ProfileScreen({ userId, onOpenGame }: Props) {
  const client = useQueryClient()
  const file = useRef<HTMLInputElement>(null)

  const profile = useQuery<Profile>({
    queryKey: ['profile', userId],
    queryFn: () => (userId === null ? fetchProfile() : fetchUser(userId)),
  })

  /*
   * Аватарку меняет только сам человек, поэтому кнопки есть лишь в своём
   * профиле. После смены обновляем и профиль, и «кто вошёл»: одну и ту же
   * картинку показывают в шапке, за столом и в списке друзей.
   */
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ['profile', userId] })
    await client.invalidateQueries({ queryKey: meKey })
  }

  const upload = useMutation({ mutationFn: uploadAvatar, onSuccess: refresh })
  const remove = useMutation({ mutationFn: removeAvatar, onSuccess: refresh })

  if (profile.isError) {
    return <p className="card">профиль не открылся — попробуйте обновить страницу</p>
  }

  if (profile.data === undefined) {
    return <p className="card">загружаем профиль…</p>
  }

  const { stats } = profile.data

  return (
    <div className="lobby">
      <div className="hero">
        {/*
         * Своя аватарка меняется нажатием на неё саму: кнопка рядом
         * говорила бы то же, что и сама картинка, только словами.
         */}
        <div className="hero__avatar">
          {userId === null ? (
            <span className="avatar-pick">
              <input
                ref={file}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={(event) => {
                  const chosen = event.target.files?.[0]

                  if (chosen !== undefined) upload.mutate(chosen)

                  // Сбрасываем поле: иначе повторный выбор того же файла
                  // не считается изменением и ничего не произойдёт.
                  event.target.value = ''
                }}
              />
              <button
                type="button"
                className="avatar-pick__open"
                aria-label="изменить аватарку"
                disabled={upload.isPending}
                onClick={() => file.current?.click()}
              >
                <Avatar
                  name={profile.data.nickname ?? profile.data.name}
                  src={profile.data.avatar}
                  size="l"
                />
              </button>

              {profile.data.avatar !== null && (
                <button
                  type="button"
                  className="ghost avatar-pick__off"
                  aria-label="убрать аватарку"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  ×
                </button>
              )}
            </span>
          ) : (
            <Avatar
              name={profile.data.nickname ?? profile.data.name}
              src={profile.data.avatar}
              size="l"
            />
          )}
        </div>

        {upload.isError && <p className="banner banner--error">{message(upload.error)}</p>}
        <h2 className="hero__title">{profile.data.nickname ?? profile.data.name}</h2>
        {/* Только когда сказать больше нечего: сыграно и побед стоят
            в карточке ниже, и повторять их подписью незачем. */}
        {stats.played === 0 && <p className="hero__lead">Партий пока нет — самое время начать.</p>}
      </div>

      {stats.buckets.length > 0 && (
        <div className="card">
          {/*
            Два числа вместо заголовка: «Статистика» над числами — подпись
            к тому, что и так очевидно, а эти два и есть то, за чем в профиль
            заходят. Ниже — разбор, из чего они складываются.
            
            Общей доли побед среди них нет намеренно: она смешивала победы над
            лёгким компьютером с победами над людьми (см. PlayerStats).
          */}
          <div className="stats stats--pair">
            <div className="stat">
              <span className="stat__value">{stats.played}</span>
              <span className="stat__label">сыграно</span>
            </div>
            <div className="stat">
              <span className="stat__value">{stats.won}</span>
              <span className="stat__label">побед</span>
            </div>
          </div>

          {/*
            Строками-полосами, а не таблицей.
            
            Таблица из четырёх чисел заставляет читать её как отчёт: глаз
            ищет столбец, потом строку, потом сравнивает. А вопрос у человека
            один — «где у меня получается лучше», — и отвечает на него длина
            полосы, без чтения вовсе.
            
            Соперник показан тем же знаком, каким его выбирают: у компьютера
            это грань кубика с числом точек по сложности (как в меню игры
            с компьютером), у людей — фишки. Так строка узнаётся раньше, чем
            прочитана.
          */}
          <ul className="rivals">
            {stats.buckets.map((bucket) => (
              <li className="rival" key={`${bucket.rival}-${bucket.seats}`}>
                <span className="rival__mark" aria-hidden="true">
                  {PIPS[bucket.rival] === null ? (
                    <PlayersIcon />
                  ) : (
                    <span className="die die--flat">
                      {/* Полным разлётом точек, а не сжатым, как в меню
                          выбора сложности: там значок мельче, и там точки
                          сводили к центру. Здесь сжатие слепляло шесть точек
                          в две полосы, и «сложный» становился неотличим от
                          «среднего» — то есть значок перестал говорить то
                          единственное, зачем он тут. */}
                      <Face value={PIPS[bucket.rival] ?? 1} />
                    </span>
                  )}
                </span>

                <span className="rival__who">
                  {RIVALS[bucket.rival]}
                  <span className="chip rival__seats" title={`игроков за столом: ${bucket.seats}`}>
                    {bucket.seats}
                  </span>
                </span>

                <span className="rival__rate">{bucket.winRate}%</span>

                {/* Полоса — доля побед длиной. Число рядом остаётся: по полосе
                    видно «лучше или хуже», а сколько именно — только числом. */}
                <span className="rival__bar">
                  <span className="rival__fill" style={{ width: `${bucket.winRate}%` }} />
                </span>

                <span className="rival__count">
                  {bucket.won} из {bucket.played}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Настройки — только в своём профиле: чужую почту менять нечем. */}
      {userId === null && <SettingsCard />}

      {stats.recent.length > 0 && (
        <div className="card">
          <h2 className="card__title">Сыгранные партии</h2>
          <ul className="games">
            {stats.recent.map((game) => (
              <li key={game.id}>
                <button type="button" className="games__name" onClick={() => onOpenGame(game.id)}>
                  {opponents(game.opponents)}
                </button>
                <span className={game.won ? 'chip chip--turn' : 'chip'}>
                  {game.won ? 'победа' : 'поражение'}
                </span>
                <span className="muted" style={{ marginLeft: 'auto' }}>
                  {game.at === null ? '' : new Date(game.at).toLocaleDateString('ru')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * Как называется соперник в строке.
 *
 * Одним словом, и слово это — уровень. «Компьютер сложный» занимал полстроки
 * и переносился второй, а слово «компьютер» в нём ничего не добавляло:
 * о том, что соперник машина, говорит грань кубика рядом — тот же знак, каким
 * сложность и выбирают. Знак отвечает «кто», слово — «какой».
 *
 * «Люди» без уточнения: за столом мог быть и один человек, и трое, а сколько
 * именно — стоит рядом в кружке.
 */
const RIVALS: Record<Rival, string> = {
  people: 'люди',
  hard: 'сложный',
  normal: 'средний',
  easy: 'лёгкий',
}

/**
 * Точек на кубике по сложности — те же, что в меню игры с компьютером.
 *
 * Не украшение: сложность там и выбирают гранью кубика, и строка статистики
 * должна узнаваться тем же знаком. null — соперник живой, кубику взяться
 * неоткуда.
 */
const PIPS: Record<Rival, number | null> = {
  people: null,
  easy: 2,
  normal: 4,
  hard: 6,
}

/** Текст ошибки от сервера: он объясняет, что не так с картинкой. */
function message(error: unknown): string {
  const response = (
    error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
  ).response

  return (
    Object.values(response?.data?.errors ?? {})[0]?.[0] ??
    response?.data?.message ??
    'картинку не приняли'
  )
}

/**
 * С кем играли — одной короткой строкой.
 *
 * Перечислять всех троих незачем: строка вылезала за карточку и сталкивала
 * метку «победа» на следующий этаж. Сколько среди них компьютеров, видно
 * выше, в общем счёте партий.
 */
function opponents(rivals: { name: string; bot: string | null }[]): string {
  const first = rivals[0]?.name

  if (first === undefined) return 'партия'

  return rivals.length > 1 ? `${first} и ещё ${rivals.length - 1}` : first
}
